import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { PROJECT_ID } from 'routes/BudgetModule/config/constants';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { HttpContentWithData, HttpResponse } from 'store/APIClient';
import { dispatch } from 'store/store';
import { uniq } from 'lodash';
import { Project } from 'routes/BudgetModule/shared/useGetProjects';
import { useAddInscriptionProjectMutation } from '../../../hooks/inscriptionsProjects';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';

type RefreshInscriptionsWithNewProjectProps = {
    newDataResponse: HttpResponse<
        HttpContentWithData<{
            projectId: number;
        }>
    >;
    queryClient: QueryClient;
    queryCacheKey: InscriptionQueryKey;
    inscriptionIdColumn?: keyof InscriptionT;
    inscriptionId: string;
    projects: Project[];
    phase?: string;
};

/**
 * A helper that refresh current inscription after a new project association
 */
const refreshInscriptionsAfterNewProjectAssociation = ({
    newDataResponse,
    queryClient,
    queryCacheKey,
    inscriptionIdColumn,
    inscriptionId,
    projects,
}: RefreshInscriptionsWithNewProjectProps) => {
    queryClient.setQueryData(
        queryCacheKey,
        (oldInscriptions: { data: InscriptionT[] }) => ({
            ...oldInscriptions,
            data: oldInscriptions.data.map((inscription) => {
                if (
                    inscriptionIdColumn &&
                    inscription[inscriptionIdColumn] === inscriptionId
                ) {
                    return {
                        ...inscription,
                        [PROJECT_ID]: uniq([
                            ...inscription[PROJECT_ID],
                            newDataResponse.content.data.projectId,
                        ]),
                    };
                }
                return inscription;
            }),
        }),
    );
    trackEventBudget(TRACKABLE_EVENTS.budget.general.associateCategory, {
        category: projects.find(
            (project: Project) =>
                project.id === newDataResponse.content.data.projectId,
        )?.name,
    });
};

/**
 * Custom hook that returns a method that :
 * 1) associate a project to an inscription,
 * 2) then triggers a (manual) refresh of cached inscriptions list.
 */
export const useAddProjectToInscription = (
    inscriptionId: string,
    projects: Project[],
    queryCacheKey: InscriptionQueryKey,
    inscriptionIdColumn?: keyof InscriptionT,
) => {
    const { mutate: addInscriptionProject } =
        useAddInscriptionProjectMutation();
    const queryClient = useQueryClient();

    const addProjectToInscription = (projectId: number) => {
        addInscriptionProject(
            {
                data: {
                    inscriptionId,
                    projectId,
                },
            },
            {
                onSuccess: (
                    response: HttpResponse<
                        HttpContentWithData<{
                            projectId: number;
                        }>
                    >,
                ) => {
                    dispatch.vision.notifyUser({
                        message: response.content.message,
                        snackType: 'success',
                    });
                    refreshInscriptionsAfterNewProjectAssociation({
                        newDataResponse: response,
                        queryClient,
                        queryCacheKey,
                        inscriptionIdColumn,
                        inscriptionId,
                        projects,
                    });
                },
                onError: (error: Error) => {
                    dispatch.vision.notifyUser({
                        message: error.message,
                        snackType: 'error',
                    });
                },
            },
        );
    };
    return { addProjectToInscription };
};
